import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseButton from './CloseModalButton';
import InputField from './InputField';
import { Address, AddressType } from '../../types/Address';
import { addNewAddress, updateAddress } from '../../services/AddressServices';
import useClickOutside from '../../hooks/useModalClickHandler';
import { isICOValid } from '../../../utils/icoValidatorCz';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from '../../../localization/useTranslations';
import { useAddresses } from '../../hooks/useAddresses';
import Checkbox from '@mui/material/Checkbox';
import { getDeliveryAddressType } from '../../../utils/getDeliveryAddressType';

interface AddressModalProps {
  onClose: () => void;
  addresses: Address[] | null;
  onAddressChange: (newAddress: Address) => void;
  onAddNewAddress: (newAddress: Address) => void;
  currentAddressType: AddressType | null;
  setCurrentAddressType: (addressType: AddressType) => void;
  isNewAddress: boolean;
  userEmail: string;
}

const AddressModal: React.FC<AddressModalProps> = ({
  onClose,
  addresses,
  onAddressChange,
  currentAddressType,
  setCurrentAddressType,
  isNewAddress,
  userEmail,
  onAddNewAddress,
}) => {
  const CURRENT_COUNTRY = window.country.toLowerCase() || 'cz';

  const translations = useTranslations();
  const { renderAddressLabelByType, emptyForm } = useAddresses();

  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(CURRENT_COUNTRY || 'cz');
  const [isFormDirty, setIsFormDirty] = useState(false);

  const validationSchema = useMemo(() => {
    let schema = yup
      .object({
        firstName: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
        lastName: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
        phoneNumber: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .matches(/^[+][-\s./0-9]{9,}$/, {
            message: translations.salesFunnel.invalidPhoneNumberMessage || 'Invalid phone number',
            excludeEmptyString: true,
          }),
        address: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .min(3, translations.salesFunnel.minLength3ErrorMessage || 'Minimum length is 3 characters')
          .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
        number: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
        zip: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .matches(/^\d{5}$/, { message: 'ZIP must be 5 digits long' }),
        city: yup
          .string()
          .required(translations.myAccountSettings?.valueRequired || 'Value is required')
          .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),

        ...(showCompanyDetails
          ? {
              companyName: yup
                .string()
                .required(translations.myAccountSettings?.valueRequired || 'Value is required')
                .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
              companyId: yup
                .string()
                .required(translations.myAccountSettings?.valueRequired || 'Value is required')
                .test('is-ICO-valid', translations.salesFunnel.invalidCompanyId || 'Invalid Company ID', (value) =>
                  isICOValid(value)
                )
                .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
              taxId: yup
                .string()
                .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
            }
          : {}),
      })
      .required();

    return schema;
  }, [
    showCompanyDetails,
    translations.salesFunnel.invalidCompanyId,
    translations.salesFunnel.invalidPhoneNumberMessage,
    translations.salesFunnel.maxLength50ErrorMessage,
    translations.salesFunnel.minLength3ErrorMessage,
    translations.myAccountSettings?.valueRequired,
  ]);

  const methods = useForm({ resolver: yupResolver(validationSchema) });
  const { reset, formState } = methods;

  // force form to update when formState.isDirty changes
  useEffect(() => {
    setIsFormDirty(formState.isDirty);
  }, [formState.isDirty]);

  const onSubmit = async (data: any) => {
    if (!isFormDirty) {
      onClose();

      return;
    }

    const formData = {
      ...data,
      email: userEmail,
      countryIso: selectedCountry.toUpperCase(),
      type: currentAddressType,
    };

    try {
      if (isNewAddress) {
        await addNewAddress(formData);

        onAddNewAddress(formData);
      } else {
        await updateAddress(formData);

        onAddressChange(formData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving address:', error);
    }
  };

  // adjust formData when switching between address types
  useEffect(() => {
    if (addresses) {
      if (!isNewAddress && currentAddressType) {
        const addressData = addresses?.find((address) => address.type === currentAddressType);

        reset({
          ...(addressData || {}),
          number: (addressData?.number ?? undefined) as string,
        });
      } else {
        reset(emptyForm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewAddress, currentAddressType]);

  // show company details if the address has a company name
  useEffect(() => {
    const companyDetails = addresses?.find((address) => address.type === currentAddressType)?.companyName;
    setShowCompanyDetails(Boolean(companyDetails));
  }, [addresses, currentAddressType]);

  const formRef = useRef<HTMLFormElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(dialogRef, onClose);
  useClickOutside(countryDropdownRef, () => setIsCountryDropdownOpen(false));

  const renderDynamicTitle = () => {
    if (isNewAddress) {
      return translations.myAccountSettings.addNewAddress || 'Add New Address';
    } else {
      return `${translations.myAccountSettings.edit || 'Edit'} ${renderAddressLabelByType(
        currentAddressType as AddressType
      )} ${translations.myAccountSettings.address || 'Address'}`;
    }
  };

  return (
    <div id="address-modal" className="settings-modal settings-modal--address modal modal--large" ref={dialogRef}>
      <div className="settings-modal__wrapper modal__wrapper">
        <div className="settings-modal__header modal__header">
          <h5 className="settings-modal__title">{renderDynamicTitle()}</h5>
          <CloseButton onClose={onClose} />
        </div>
        <FormProvider {...methods}>
          {/* Provides form context to all inputs */}
          <form
            className="settings-modal__form"
            id="address-modal-form"
            data-type="add"
            onSubmit={methods.handleSubmit(onSubmit)}
            ref={formRef}
          >
            <div className="settings-modal__content modal__content">
              {/* Radio Buttons for Address Type */}
              <p className="settings-modal__radio-title basic basic-16">
                {translations.myAccountSettings.addressType || 'Type of Address'}
              </p>
              <div className="settings-modal__radio-wrapper">
                {[getDeliveryAddressType(window.country), 'shop', 'invoice'].map((addressType) => (
                  <label htmlFor={addressType} className="settings-modal__radio button-text" key={addressType}>
                    {renderAddressLabelByType(addressType as AddressType)}
                    <input
                      className="settings-modal__radio-input"
                      type="radio"
                      id={addressType}
                      name="addressType"
                      value={addressType}
                      checked={currentAddressType === addressType}
                      onChange={() => setCurrentAddressType(addressType as AddressType)}
                    />
                  </label>
                ))}
              </div>

              {/* Split Row for First and Last Name */}
              <div className="settings-modal__row settings-modal__row--split">
                <InputField
                  type="text"
                  name="firstName"
                  label={translations.myAccountSettings.firstName || 'First Name'}
                />
                <InputField
                  type="text"
                  name="lastName"
                  label={translations.myAccountSettings.lastName || 'Last Name'}
                />
              </div>

              <div className="settings-modal__row">
                <InputField
                  type="text"
                  name="phoneNumber"
                  label={translations.myAccountSettings.phoneNumber || 'Phone'}
                />
              </div>

              {/* Address and City */}
              <div className="settings-modal__row settings-modal__row--split">
                <InputField type="text" name="address" label={translations.myAccountSettings.streetName || 'Street'} />
                <InputField
                  type="text"
                  name="number"
                  label={translations.myAccountSettings.houseNumber || 'House Number'}
                />
              </div>

              <div className="settings-modal__row settings-modal__row--split">
                <InputField type="text" name="city" label={translations.myAccountSettings.city || 'City'} />
                <InputField type="text" name="zip" label={translations.myAccountSettings.zipCode || 'ZIP'} />
              </div>

              <div className="settings-modal__row">
                <div className={`settings-modal__country-selector-wrapper `} ref={countryDropdownRef}>
                  <label className="settings-modal__country-selector-label input-label">
                    {translations.myAccountSettings.country || 'Country'}
                  </label>
                  <div
                    id="country-selector"
                    className="settings-modal__country-selector"
                    onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
                  >
                    <span className="settings-modal__country-selector-value basic basic-16">
                      {selectedCountry === 'cz'
                        ? translations.general.czechRepublic || 'Czech Republic'
                        : translations.general.slovakia || 'Slovakia'}
                    </span>
                    <div
                      className={`settings-modal__country-selector-arrow ${isCountryDropdownOpen ? 'isOpen' : ''}`}
                    />
                  </div>
                  {isCountryDropdownOpen && (
                    <div className="settings-modal__country-selector-dropdown">
                      <div className="settings-modal__country-selector-text basic change-blocked-field visible">
                        <p
                          dangerouslySetInnerHTML={{
                            __html:
                              translations.myAccountSettings?.countrySelectorMessage ||
                              "We can’t change the delivery address on existing subscription. In case you need to change it please contact us on <a href='mailto:publisher@forbes.cz'>publisher@forbes.cz.</a>.",
                          }}
                        />
                      </div>
                      <div className="settings-modal__country-selector-divider delivery-field change-blocked-field visible" />
                      <div
                        className={`settings-modal__country-selector-option-wrapper ${
                          CURRENT_COUNTRY === 'sk' ? '' : 'disabled'
                        }`}
                        onClick={() => {
                          setSelectedCountry('sk');
                        }}
                      >
                        <span className="settings-modal__country-selector-option link">
                          {translations.general.slovakia || 'Slovakia'}
                        </span>
                      </div>
                      <div
                        className={`settings-modal__country-selector-option-wrapper ${
                          CURRENT_COUNTRY === 'cz' ? '' : 'disabled'
                        }`}
                        onClick={() => {
                          setSelectedCountry('cz');
                          setIsCountryDropdownOpen(false);
                        }}
                      >
                        <span className="settings-modal__country-selector-option link">
                          {translations.general.czechRepublic || 'Czech Republic'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Conditional Company Details */}

              <div className="settings-modal__divider"></div>

              <div className="settings-modal__company-details-wrapper">
                <label className="settings-modal__company-toggle" htmlFor="company-details-toggle">
                  <Checkbox
                    id="company-details-toggle"
                    name="company"
                    className="settings-modal__company-toggle-input"
                    checked={showCompanyDetails}
                    onChange={() => setShowCompanyDetails((prevState) => !prevState)}
                    sx={{
                      color: '#475569',
                      borderRadius: 0,
                      '&.Mui-checked': { color: '#B45309' },
                      '& .MuiSvgIcon-root': { fontSize: 24 },
                      '& .MuiIconButton-root': {
                        width: 24,
                        height: 24,
                        borderRadius: 0,
                      },
                    }}
                  />
                  <span className="link-16">
                    {translations.myAccountSettings.companyInformation || 'Company Information'}
                  </span>
                </label>

                {showCompanyDetails && (
                  <div className="settings-modal__company-details">
                    <div className="settings-modal__row">
                      <InputField
                        required
                        type="text"
                        name="companyName"
                        label={translations.myAccountSettings.companyName || 'Company'}
                      />
                    </div>

                    <div className="settings-modal__row settings-modal__row--split">
                      <InputField
                        required
                        type="text"
                        name="companyId"
                        label={translations.myAccountSettings.ico || 'ICO'}
                      />

                      <InputField
                        required
                        type="text"
                        name="taxId"
                        label={translations.myAccountSettings.dic || 'DIC'}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer with buttons */}
              <div className="settings-modal__footer modal__footer" id="settings-modal-address-footer">
                {isNewAddress ? (
                  <button type="submit" className="settings-modal__button link link--medium">
                    {translations.myAccountSettings.addNewAddress || 'Add New Address'}
                  </button>
                ) : (
                  <button type="submit" className="settings-modal__button link-16">
                    {translations.myAccountSettings.saveChanges || 'Save Changes'}
                  </button>
                )}
                <button
                  id="address_modal_cancel"
                  type="reset"
                  className="settings-modal__button link-16"
                  onClick={onClose}
                >
                  {translations.myAccountSettings.cancelAddress || 'Cancel'}
                </button>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddressModal;
